import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  InputAdornment,
  Tooltip,
  Badge,
  Collapse,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import HotelIcon from '@mui/icons-material/Hotel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DrawIcon from '@mui/icons-material/Draw';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import GavelIcon from '@mui/icons-material/Gavel';
import ArticleIcon from '@mui/icons-material/Article';
import PaymentsIcon from '@mui/icons-material/Payments';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─────────────────────────────────────────────────────────────────────────────
// Componente reutilizable: canvas de firma
// ─────────────────────────────────────────────────────────────────────────────
function SignatureCanvas({ canvasRef, onHasSignatureChange }) {
  const [isDrawing, setIsDrawing] = useState(false);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    onHasSignatureChange(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onHasSignatureChange(false);
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Paper
        elevation={3}
        sx={{
          display: 'inline-block',
          p: 1,
          border: '2px dashed',
          borderColor: 'primary.main',
          width: '100%',
          maxWidth: 500,
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={500}
          height={160}
          style={{
            border: '1px solid #ccc',
            backgroundColor: 'white',
            width: '100%',
            height: 'auto',
            maxWidth: '100%',
            touchAction: 'none',
            cursor: 'crosshair',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </Paper>
      <Box sx={{ mt: 1 }}>
        <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={clearCanvas}>
          Limpiar firma
        </Button>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export function AdministracionReservas() {
  const [rooms, setRooms] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingDialogOpen, setEditingDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [resendingVoucherId, setResendingVoucherId] = useState(null);

  // ── Canvas ref para dibujar la firma ─────────────────────────────────────
  const canvasPoliciesRef = useRef(null);
  const [hasSignaturePolicies, setHasSignaturePolicies] = useState(false);

  // ── IMPORTANTE: dataURL guardado al avanzar del paso 1 ───────────────────
  const [signatureDataURL, setSignatureDataURL] = useState(null);

  const [generatingCheckin, setGeneratingCheckin] = useState(false);

  // ── Pasos del check-in (3 pasos) ─────────────────────────────────────────
  const [checkinStep, setCheckinStep] = useState(1);
  const checkinSteps = ['Políticas del hotel', 'Datos Reserva', 'Confirmar Check-in'];

  // ── Info adicional ────────────────────────────────────────────────────────
  const [additionalInfo, setAdditionalInfo] = useState({
    ciudad: '',
    estado: '',
    includeBreakfast: false,
    markSecondNightPaid: false,   // ← NUEVO: marcar 2da noche como pagada al hacer check-in
  });

  // ── Filtros ───────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    paymentStatus: [],
    lugar: [],
    dateRange: {
      checkInStart: null,
      checkInEnd: null,
      checkOutStart: null,
      checkOutEnd: null,
    },
    nightsRange: [1, 30],
    amountRange: [0, 10000],
    hasSecondNightPaid: null,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchRooms();
    fetchReservas();
  }, []);

  useEffect(() => {
    let count = 0;
    if (filters.search.trim()) count++;
    if (filters.status.length > 0) count++;
    if (filters.paymentStatus.length > 0) count++;
    if (filters.lugar.length > 0) count++;
    if (filters.dateRange.checkInStart || filters.dateRange.checkInEnd) count++;
    if (filters.dateRange.checkOutStart || filters.dateRange.checkOutEnd) count++;
    if (filters.nightsRange[0] > 1 || filters.nightsRange[1] < 30) count++;
    if (filters.amountRange[0] > 0 || filters.amountRange[1] < 10000) count++;
    if (filters.hasSecondNightPaid !== null) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  async function fetchRooms() {
    try {
      const response = await fetch(`${API_URL}/api/rooms/admin`);
      if (!response.ok) throw new Error('Error al cargar habitaciones');
      const data = await response.json();
      setRooms(data);
    } catch (err) {
      console.error('Error al cargar habitaciones:', err);
    }
  }

  async function fetchReservas() {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Error al cargar reservas');
      const data = await response.json();
      setReservas(data);
    } catch (err) {
      console.error('Error al cargar reservas:', err);
      setError('No se pudieron cargar las reservas');
    } finally {
      setLoading(false);
    }
  }

  function parseBookingDate(dateValue) {
    if (!dateValue) return null;
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      const datePart = dateValue.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateValue);
  }

  const filteredReservas = useMemo(() => {
    return reservas.filter((reserva) => {
      if (filters.search) {
        const term = filters.search.toLowerCase();
        const id = (reserva.bookingId || '').toLowerCase();
        const name = `${reserva.guestInfo?.firstName || ''} ${reserva.guestInfo?.lastName || ''}`.toLowerCase();
        const email = (reserva.guestInfo?.email || '').toLowerCase();
        const room = (reserva.roomName || '').toLowerCase();
        if (!id.includes(term) && !name.includes(term) && !email.includes(term) && !room.includes(term)) return false;
      }
      if (filters.status.length > 0 && !filters.status.includes(reserva.status)) return false;
      if (filters.paymentStatus.length > 0 && !filters.paymentStatus.includes(reserva.paymentStatus)) return false;
      if (filters.lugar.length > 0) {
        const room = rooms.find((r) => r._id === reserva.roomId);
        if (!filters.lugar.includes(room?.lugar || '')) return false;
      }
      if (filters.dateRange.checkInStart || filters.dateRange.checkInEnd) {
        const checkIn = parseBookingDate(reserva.checkIn);
        if (filters.dateRange.checkInStart && checkIn < filters.dateRange.checkInStart) return false;
        if (filters.dateRange.checkInEnd && checkIn > filters.dateRange.checkInEnd) return false;
      }
      if (filters.dateRange.checkOutStart || filters.dateRange.checkOutEnd) {
        const checkOut = parseBookingDate(reserva.checkOut);
        if (filters.dateRange.checkOutStart && checkOut < filters.dateRange.checkOutStart) return false;
        if (filters.dateRange.checkOutEnd && checkOut > filters.dateRange.checkOutEnd) return false;
      }
      if (reserva.nights < filters.nightsRange[0] || reserva.nights > filters.nightsRange[1]) return false;
      if (reserva.totalPrice < filters.amountRange[0] || reserva.totalPrice > filters.amountRange[1]) return false;
      if (filters.hasSecondNightPaid !== null && filters.hasSecondNightPaid !== reserva.secondNightNotePaid) return false;
      return true;
    });
  }, [reservas, filters, rooms]);

  const clearAllFilters = () => {
    setFilters({
      search: '',
      status: [],
      paymentStatus: [],
      lugar: [],
      dateRange: { checkInStart: null, checkInEnd: null, checkOutStart: null, checkOutEnd: null },
      nightsRange: [1, 30],
      amountRange: [0, 10000],
      hasSecondNightPaid: null,
    });
  };

  function openView(res) {
    setSelected(res);
    setViewDialogOpen(true);
  }

  function toInputDateFormat(dateValue) {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) return dateValue;
    if (typeof dateValue === 'string' && dateValue.includes('T')) return dateValue.split('T')[0];
    return new Date(dateValue).toISOString().slice(0, 10);
  }

  function openEdit(res) {
    setSelected(res);
    setEditForm({ ...res, checkIn: toInputDateFormat(res.checkIn), checkOut: toInputDateFormat(res.checkOut) });
    setEditingDialogOpen(true);
  }

  // ── Abrir diálogo de check-in: resetear TODO ──────────────────────────────
  function openSignatureDialog(res) {
    setSelected(res);
    setSignatureDialogOpen(true);
    setCheckinStep(1);
    setHasSignaturePolicies(false);
    setSignatureDataURL(null);
    setAdditionalInfo({
      ciudad: '',
      estado: '',
      includeBreakfast: false,
      markSecondNightPaid: false,
    });

    setTimeout(() => {
      if (canvasPoliciesRef.current) {
        const ctx = canvasPoliciesRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasPoliciesRef.current.width, canvasPoliciesRef.current.height);
      }
    }, 100);
  }

  function closeSignatureDialog() {
    setSignatureDialogOpen(false);
    setCheckinStep(1);
    setHasSignaturePolicies(false);
    setSignatureDataURL(null);
    setAdditionalInfo({
      ciudad: '',
      estado: '',
      includeBreakfast: false,
      markSecondNightPaid: false,
    });
    if (canvasPoliciesRef.current) {
      const ctx = canvasPoliciesRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasPoliciesRef.current.width, canvasPoliciesRef.current.height);
    }
  }

  // ── Validación por paso ───────────────────────────────────────────────────
  function canProceedFromStep(step) {
    if (step === 1) return hasSignaturePolicies;
    if (step === 2) return additionalInfo.ciudad.trim() !== '' && additionalInfo.estado.trim() !== '';
    return false;
  }

  // ── Avanzar de paso capturando la firma en el paso 1 ─────────────────────
  function handleNextStep() {
    if (checkinStep === 1) {
      if (canvasPoliciesRef.current) {
        const dataURL = canvasPoliciesRef.current.toDataURL('image/png');
        setSignatureDataURL(dataURL);
      }
    }
    setCheckinStep((s) => s + 1);
  }

  // ── Generar PDF ───────────────────────────────────────────────────────────
  async function generateCheckinPDF() {
    if (!hasSignaturePolicies && !signatureDataURL) {
      setError('Por favor, firma las políticas antes de generar el documento');
      return;
    }
    if (!additionalInfo.ciudad.trim() || !additionalInfo.estado.trim()) {
      setError('Por favor, completa la ciudad y el estado');
      return;
    }

    setGeneratingCheckin(true);
    setError(null);

    try {
      const signatureBase64 =
        signatureDataURL ||
        (canvasPoliciesRef.current ? canvasPoliciesRef.current.toDataURL('image/png') : null);

      if (!signatureBase64) {
        throw new Error('No se pudo obtener la firma. Por favor, regresa al paso 1 y firma de nuevo.');
      }

      const token = localStorage.getItem('authToken');

      // ── Si marcaron 2da noche como pagada, llamar al endpoint primero ──
      if (additionalInfo.markSecondNightPaid && selected?.paymentStatus === 'partial' && !selected?.secondNightNotePaid) {
        const markPaidResponse = await fetch(`${API_URL}/api/bookings/${selected.bookingId}/mark-paid`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!markPaidResponse.ok) {
          const errData = await markPaidResponse.json();
          throw new Error(errData.message || 'Error al marcar segunda noche como pagada');
        }
        // Refrescar reservas en background
        fetchReservas();
      }

      const response = await fetch(`${API_URL}/api/bookings/generate-checkin/${selected.bookingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          signature: signatureBase64,
          ciudad: additionalInfo.ciudad.trim(),
          estado: additionalInfo.estado.trim(),
          includeBreakfast: Boolean(additionalInfo.includeBreakfast),
          markSecondNightPaid: Boolean(additionalInfo.markSecondNightPaid),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar check-in');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Checkin_${selected.bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      const msg = additionalInfo.markSecondNightPaid
        ? 'Check-in PDF generado y segunda noche marcada como pagada'
        : 'Check-in PDF generado exitosamente (3 páginas)';
      setSuccess(msg);
      closeSignatureDialog();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Error al generar check-in:', err);
      setError(err.message || 'No se pudo generar el check-in');
    } finally {
      setGeneratingCheckin(false);
    }
  }

  async function handleSaveEdit() {
    if (!editForm) return;
    if (new Date(editForm.checkOut) <= new Date(editForm.checkIn)) {
      setError('Fecha check-out debe ser después de check-in');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${editForm.bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          checkIn: editForm.checkIn,
          checkOut: editForm.checkOut,
          roomId: editForm.roomId,
          guestInfo: editForm.guestInfo,
          status: editForm.status,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar reserva');
      }
      setSuccess('Reserva actualizada exitosamente');
      setEditingDialogOpen(false);
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelReserva(res) {
    if (!window.confirm('¿Estás seguro de cancelar esta reserva?')) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${res.bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (!response.ok) throw new Error('Error al cancelar reserva');
      setSuccess('Reserva cancelada exitosamente');
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo cancelar la reserva');
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkSecondNightPaid(res) {
    if (!window.confirm('¿Marcar segunda noche como pagada?')) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${res.bookingId}/mark-paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al marcar como pagado');
      setSuccess('Segunda noche marcada como pagada');
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo marcar como pagado');
    } finally {
      setLoading(false);
    }
  }

  async function handleUnmarkSecondNightPaid(res) {
    if (!window.confirm('¿Anular pago de la segunda noche?')) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${res.bookingId}/unmark-paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al anular pago');
      setSuccess('Pago de segunda noche anulado');
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo anular el pago');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadVoucher(bookingId) {
    try {
      const response = await fetch(`${API_URL}/api/bookings/download/${bookingId}`);
      if (!response.ok) throw new Error('Error al descargar voucher');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Voucher_${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess('Voucher descargado exitosamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo descargar el voucher');
    }
  }

  async function handleResendVoucher(bookingId, guestEmail) {
    setError(null);
    setSuccess(null);
    setResendingVoucherId(bookingId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/resend-voucher`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'No se pudo reenviar el voucher');
      setSuccess(`Voucher reenviado a ${guestEmail || 'huésped'} correctamente`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'No se pudo reenviar el voucher');
    } finally {
      setResendingVoucherId(null);
    }
  }

  function getRoomName(id) {
    return rooms.find((r) => r._id === id)?.name || '—';
  }

  function chipColorForStatus(status) {
    if (status === 'cancelled') return 'error';
    if (status === 'active') return 'success';
    return 'default';
  }

  function chipColorForPayment(paymentStatus) {
    if (paymentStatus === 'completed') return 'success';
    if (paymentStatus === 'partial') return 'warning';
    return 'default';
  }

  function formatDate(date) {
    if (!date) return '—';
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    if (typeof date === 'string' && date.includes('T')) {
      const [year, month, day] = date.split('T')[0].split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    return new Date(date).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  function formatDateLong(date) {
    if (!date) return '-';
    try {
      const str = typeof date === 'string' && date.includes('T') ? date.split('T')[0] : date;
      const [year, month, day] = String(str).split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return String(date);
    }
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
  }

  const stats = useMemo(() => ({
    total: filteredReservas.length,
    active: filteredReservas.filter((r) => r.status === 'active').length,
    cancelled: filteredReservas.filter((r) => r.status === 'cancelled').length,
    completedPayment: filteredReservas.filter((r) => r.paymentStatus === 'completed').length,
    partialPayment: filteredReservas.filter((r) => r.paymentStatus === 'partial').length,
    totalRevenue: filteredReservas.reduce((sum, r) => sum + (r.totalPrice || 0), 0),
    pendingSecondNight: filteredReservas.filter((r) => r.paymentStatus === 'partial' && !r.secondNightNotePaid).length,
  }), [filteredReservas]);

  // ─────────────────────────────────────────────────────────────────────────
  // Sub-componentes de filtros
  // ─────────────────────────────────────────────────────────────────────────
  const AdvancedFilters = () => (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'primary.light' }}><TuneIcon /></Avatar>}
        title={
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Filtros Avanzados</Typography>
            <Badge badgeContent={activeFiltersCount} color="primary"><FilterAltIcon color="action" /></Badge>
          </Stack>
        }
        subheader="Aplica múltiples criterios de búsqueda"
        action={
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={clearAllFilters} disabled={activeFiltersCount === 0}><FilterAltOffIcon /></IconButton>
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        }
      />
      <Collapse in={showFilters}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Fechas de Check-in</Typography>
              <Stack direction="row" spacing={2}>
                <TextField type="date" label="Desde" value={filters.dateRange.checkInStart ? filters.dateRange.checkInStart.toISOString().slice(0, 10) : ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, checkInStart: e.target.value ? new Date(e.target.value) : null } }))}
                  InputLabelProps={{ shrink: true }} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthIcon fontSize="small" /></InputAdornment> }} />
                <TextField type="date" label="Hasta" value={filters.dateRange.checkInEnd ? filters.dateRange.checkInEnd.toISOString().slice(0, 10) : ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, checkInEnd: e.target.value ? new Date(e.target.value) : null } }))}
                  InputLabelProps={{ shrink: true }} fullWidth />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Fechas de Check-out</Typography>
              <Stack direction="row" spacing={2}>
                <TextField type="date" label="Desde" value={filters.dateRange.checkOutStart ? filters.dateRange.checkOutStart.toISOString().slice(0, 10) : ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, checkOutStart: e.target.value ? new Date(e.target.value) : null } }))}
                  InputLabelProps={{ shrink: true }} fullWidth />
                <TextField type="date" label="Hasta" value={filters.dateRange.checkOutEnd ? filters.dateRange.checkOutEnd.toISOString().slice(0, 10) : ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, checkOutEnd: e.target.value ? new Date(e.target.value) : null } }))}
                  InputLabelProps={{ shrink: true }} fullWidth />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Noches: {filters.nightsRange[0]} - {filters.nightsRange[1]}</Typography>
              <Slider value={filters.nightsRange} onChange={(_, v) => setFilters(prev => ({ ...prev, nightsRange: v }))} valueLabelDisplay="auto" min={1} max={30} step={1}
                marks={[{ value: 1, label: '1' }, { value: 7, label: '7' }, { value: 14, label: '14' }, { value: 30, label: '30' }]} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Monto: {formatCurrency(filters.amountRange[0])} - {formatCurrency(filters.amountRange[1])}</Typography>
              <Slider value={filters.amountRange} onChange={(_, v) => setFilters(prev => ({ ...prev, amountRange: v }))} valueLabelDisplay="auto" min={0} max={10000} step={100}
                valueLabelFormat={(v) => formatCurrency(v)} marks={[{ value: 0, label: '$0' }, { value: 5000, label: '$5k' }, { value: 10000, label: '$10k' }]} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>Segunda noche pagada</Typography>
                <RadioGroup value={filters.hasSecondNightPaid !== null ? filters.hasSecondNightPaid.toString() : 'all'}
                  onChange={(e) => setFilters(prev => ({ ...prev, hasSecondNightPaid: e.target.value === 'all' ? null : e.target.value === 'true' }))}>
                  <Stack direction="row" spacing={2}>
                    <FormControlLabel value="all" control={<Radio size="small" />} label="Todos" />
                    <FormControlLabel value="true" control={<Radio size="small" />} label={<Stack direction="row" alignItems="center" spacing={0.5}><CheckCircleIcon fontSize="small" color="success" /><span>Sí</span></Stack>} />
                    <FormControlLabel value="false" control={<Radio size="small" />} label={<Stack direction="row" alignItems="center" spacing={0.5}><PendingIcon fontSize="small" color="warning" /><span>No</span></Stack>} />
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>Lugar</Typography>
                <FormGroup>
                  {['casaHotel', 'boutique'].map((lugar) => (
                    <FormControlLabel key={lugar}
                      control={<Checkbox size="small" checked={filters.lugar.includes(lugar)}
                        onChange={(e) => setFilters(prev => ({ ...prev, lugar: e.target.checked ? [...prev.lugar, lugar] : prev.lugar.filter((l) => l !== lugar) }))} />}
                      label={<Stack direction="row" alignItems="center" spacing={1}><HotelIcon fontSize="small" /><Typography variant="body2">{lugar === 'casaHotel' ? 'Casa Hotel' : 'Boutique'}</Typography></Stack>} />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );

  const ActiveFilterChips = () => (
    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }} alignItems="center">
      <Typography variant="caption" color="textSecondary">Filtros activos:</Typography>
      {filters.search && <Chip label={`Buscar: "${filters.search}"`} size="small" onDelete={() => setFilters(prev => ({ ...prev, search: '' }))} deleteIcon={<ClearIcon />} />}
      {filters.status.map((s) => <Chip key={s} label={`Estado: ${s === 'active' ? 'Activa' : 'Cancelada'}`} size="small" color={s === 'active' ? 'success' : 'error'}
        onDelete={() => setFilters(prev => ({ ...prev, status: prev.status.filter((x) => x !== s) }))} deleteIcon={<ClearIcon />} />)}
      {filters.paymentStatus.map((p) => <Chip key={p} label={`Pago: ${p === 'completed' ? 'Completo' : 'Parcial'}`} size="small" color={p === 'completed' ? 'success' : 'warning'}
        onDelete={() => setFilters(prev => ({ ...prev, paymentStatus: prev.paymentStatus.filter((x) => x !== p) }))} deleteIcon={<ClearIcon />} />)}
      {filters.lugar.map((l) => <Chip key={l} label={`Lugar: ${l === 'casaHotel' ? 'Casa Hotel' : 'Boutique'}`} size="small" color="info"
        onDelete={() => setFilters(prev => ({ ...prev, lugar: prev.lugar.filter((x) => x !== l) }))} deleteIcon={<ClearIcon />} />)}
      {activeFiltersCount > 0 && <Button size="small" startIcon={<ClearIcon />} onClick={clearAllFilters} sx={{ ml: 1 }}>Limpiar todos</Button>}
    </Stack>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Contenido de los pasos del check-in
  // ─────────────────────────────────────────────────────────────────────────

  // PASO 1: Todas las políticas + firma
  const StepPoliciesContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" icon={<GavelIcon />}>
        <Typography variant="subtitle2" fontWeight="bold">
          Por favor, lee con atención todas las políticas del hotel.
        </Typography>
        <Typography variant="caption">
          Al firmar al final, confirmas haber leído y aceptado la totalidad de las políticas.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, maxHeight: 380, overflowY: 'auto', bgcolor: 'grey.50' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
          Políticas La Capilla Hotel
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ mb: 2, fontStyle: 'italic' }}>
          Un gusto saludarte y esperamos que te encuentres muy bien, gracias por escogernos como destino para realizar tu viaje...
        </Typography>

        {[
          {
            title: '1- Check-in / Check-out',
            items: [
              'El check-in es a partir de las 15:00 hrs (sujeto a disponibilidad en caso de ingreso anticipado).',
              'El check-out es a las 12:00 hrs.',
              'Salida tardía: De 12:30 a 17:00 hrs: $500.00 MXN por persona.',
              'A partir de las 17:01 hrs: se cobrará una noche completa.',
              'Todo late check-out está sujeto a disponibilidad.',
            ],
          },
          {
            title: '2- Pagos y cancelaciones',
            items: [
              'El total de la reservación deberá liquidarse al momento del check-in.',
              'Cancelaciones con al menos 48 horas de anticipación reciben reembolso del 100% (3 a 5 días hábiles).',
              'Cancelaciones con menos de 48 horas generan una penalización del 50% del total de la reservación.',
              'En temporada alta (Semana Santa, días festivos y fechas especiales), se requiere aviso con al menos 15 días hábiles.',
              'Las políticas pueden variar según temporada y serán confirmadas al momento de la reservación.',
            ],
          },
          {
            title: '3- Conducta del huésped',
            items: [
              'Las habitaciones y espacios cerrados son 100% libres de humo.',
              'No se permite ruido excesivo a partir de las 22:00 hrs en habitaciones, pasillos y áreas comunes.',
              'No se permite el uso de bocinas o equipos de audio a alto volumen dentro de las habitaciones.',
              'No se permite realizar reuniones o fiestas no autorizadas dentro del hotel.',
              'No se permite el consumo de sustancias ilegales dentro de las instalaciones.',
              'En caso de incumplimiento, el hotel podrá cancelar la estancia sin derecho a reembolso y solicitar el retiro inmediato del huésped.',
            ],
          },
          {
            title: '4- Derecho de admisión y permanencia',
            items: [
              'La Capilla Hotel se reserva el derecho de admisión y permanencia.',
              'En caso de conductas agresivas, faltas de respeto al personal, daños a instalaciones o afectación a otros huéspedes, el hotel podrá cancelar la estancia sin reembolso, solicitar el retiro inmediato y aplicar cargos correspondientes.',
            ],
          },
          {
            title: '5- Responsabilidad por daños',
            items: [
              'Cualquier daño causado a habitaciones, mobiliario, jardín, piscina, jacuzzi o instalaciones será responsabilidad del titular de la reservación.',
              'El huésped se compromete a cubrir el costo total de reparación o reposición.',
            ],
          },
          {
            title: '6- Autorización de cargo a tarjeta',
            items: [
              'El huésped autoriza expresamente a La Capilla Hotel a realizar cargos a la tarjeta proporcionada como garantía en caso de: daños a instalaciones, servicios adicionales no liquidados, penalizaciones por incumplimiento o limpieza profunda o especializada.',
            ],
          },
          {
            title: '7- Uso de instalaciones',
            items: [
              'El uso de piscina y jacuzzi es bajo responsabilidad del huésped.',
              'No se permite arrojar objetos, alimentos, bebidas o cualquier elemento que dañe los sistemas o instalaciones.',
              'El hotel podrá restringir el uso de estas áreas en caso de mal uso.',
            ],
          },
          {
            title: '8- Alimentos y bebidas externas',
            items: [
              'No se permite el ingreso de alimentos o bebidas externas, salvo autorización expresa del hotel.',
              'El incumplimiento podrá generar cargos adicionales o cancelación de la estancia.',
            ],
          },
          {
            title: '9- Capacidad y personas adicionales',
            items: [
              'Se deberá respetar la capacidad máxima de cada habitación.',
              'Se cobrará $500.00 MXN por persona adicional por la noche.',
            ],
          },
          {
            title: '10- Mascotas',
            items: ['No se aceptan mascotas dentro de las instalaciones.'],
          },
          {
            title: '11- Responsabilidad sobre pertenencias',
            items: [
              'La Capilla Hotel no se hace responsable por robo, pérdida o daño de pertenencias personales.',
              'El hotel no se hace responsable por daños a vehículos dentro del estacionamiento.',
              'La Capilla Hotel no se hace responsable por objetos olvidados, extraviados o dejados dentro de las instalaciones o habitaciones.',
              'En caso de localizar algún objeto, el hotel podrá resguardarlo por un periodo máximo de 15 días naturales. Transcurrido dicho plazo, el hotel podrá disponer de los objetos sin responsabilidad alguna.',
              'El envío o recuperación de dichos objetos será responsabilidad del huésped, incluyendo costos de mensajería o traslado.',
            ],
          },
          {
            title: '12- Menores de edad',
            items: [
              'Los menores de edad deberán estar supervisados en todo momento por un adulto.',
              'El hotel no se hace responsable por accidentes derivados de falta de supervisión.',
            ],
          },
          {
            title: '13- Servicios adicionales',
            items: [
              'Los servicios adicionales requieren supervisión previa.',
              'Cancelaciones con menos de 2 horas de anticipación generarán cargo completo.',
            ],
          },
        ].map((section) => (
          <Box key={section.title} sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>{section.title}</Typography>
            {section.items.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ ml: 2, color: 'text.secondary', fontSize: '0.78rem' }}>• {item}</Typography>
            ))}
          </Box>
        ))}

        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
          Declaro haber leído y aceptado las políticas de estancia de La Capilla Hotel, así como mi responsabilidad sobre el cumplimiento de las mismas.
        </Typography>
      </Paper>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Firma del huésped — Aceptación de políticas
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Al firmar confirmas haber leído y aceptado la totalidad de las políticas del hotel (puntos 1 al 13).
          Esta firma se incluirá en la hoja 1 (check-in) y en la hoja 3 (políticas continuación) del PDF.
        </Typography>
        <SignatureCanvas canvasRef={canvasPoliciesRef} onHasSignatureChange={setHasSignaturePolicies} />
        {!hasSignaturePolicies && (
          <Alert severity="warning" sx={{ mt: 1 }}>Debes firmar para continuar al siguiente paso.</Alert>
        )}
      </Box>
    </Box>
  );

  // PASO 2: Datos de la reserva + info adicional + 2da noche (si aplica)
  const StepReservationDataContent = () => {
    // ¿Aplica la opción de segunda noche?
    const showSecondNightOption =
      selected?.paymentStatus === 'partial' && !selected?.secondNightNotePaid;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {selected && (
          <Alert severity="info">
            <Typography variant="body2"><strong>Reserva:</strong> {selected.bookingId}</Typography>
            <Typography variant="body2"><strong>Huésped:</strong> {selected.guestInfo?.firstName} {selected.guestInfo?.lastName}</Typography>
            <Typography variant="body2"><strong>Habitación:</strong> {selected.roomName}</Typography>
            <Typography variant="body2"><strong>Check-in:</strong> {formatDateLong(selected.checkIn)}</Typography>
            <Typography variant="body2"><strong>Check-out:</strong> {formatDateLong(selected.checkOut)}</Typography>
            <Typography variant="body2"><strong>Total:</strong> {formatCurrency(selected.totalPrice)}</Typography>
            <Typography variant="body2">
              <strong>Estado de pago:</strong>{' '}
              <Chip
                label={selected.paymentStatus === 'completed' ? 'Completo' : selected.paymentStatus === 'partial' ? 'Parcial' : selected.paymentStatus}
                size="small"
                color={chipColorForPayment(selected.paymentStatus)}
                sx={{ ml: 0.5 }}
              />
            </Typography>
            {selected.paymentStatus === 'partial' && (
              <>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Anticipo pagado:</strong> {formatCurrency(selected.initialPayment)}
                </Typography>
                <Typography variant="body2">
                  <strong>Saldo pendiente:</strong> {formatCurrency(selected.secondNightPayment)}
                </Typography>
              </>
            )}
          </Alert>
        )}

        <Typography variant="subtitle1" fontWeight="bold">Información adicional del huésped</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Ciudad *" value={additionalInfo.ciudad}
              onChange={(e) => setAdditionalInfo((prev) => ({ ...prev, ciudad: e.target.value }))}
              required error={!additionalInfo.ciudad.trim()}
              helperText={!additionalInfo.ciudad.trim() ? 'Campo requerido' : ''}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityIcon fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Estado *" value={additionalInfo.estado}
              onChange={(e) => setAdditionalInfo((prev) => ({ ...prev, estado: e.target.value }))}
              required error={!additionalInfo.estado.trim()}
              helperText={!additionalInfo.estado.trim() ? 'Campo requerido' : ''}
              InputProps={{ startAdornment: <InputAdornment position="start"><MapIcon fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox checked={additionalInfo.includeBreakfast}
                  onChange={(e) => setAdditionalInfo((prev) => ({ ...prev, includeBreakfast: e.target.checked }))} />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <RestaurantIcon fontSize="small" color={additionalInfo.includeBreakfast ? 'primary' : 'action'} />
                  <Typography variant="body1">¿Incluye desayuno?</Typography>
                </Stack>
              }
            />
          </Grid>

          {/* ── SEGUNDA NOCHE: solo si pago es parcial y aún no está pagada ── */}
          {showSecondNightOption && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: additionalInfo.markSecondNightPaid ? 'success.50' : 'warning.50',
                  borderColor: additionalInfo.markSecondNightPaid ? 'success.main' : 'warning.main',
                  borderWidth: 1.5,
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <PaymentsIcon color={additionalInfo.markSecondNightPaid ? 'success' : 'warning'} sx={{ mt: 0.3 }} />
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight="bold" color={additionalInfo.markSecondNightPaid ? 'success.dark' : 'warning.dark'}>
                      Saldo pendiente — Segunda noche
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      El huésped tiene un saldo pendiente de{' '}
                      <strong>{formatCurrency(selected?.secondNightPayment || 0)}</strong>.
                      ¿El huésped liquida el saldo en este check-in?
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={additionalInfo.markSecondNightPaid}
                          onChange={(e) => setAdditionalInfo((prev) => ({ ...prev, markSecondNightPaid: e.target.checked }))}
                          color="success"
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CheckCircleIcon fontSize="small" color={additionalInfo.markSecondNightPaid ? 'success' : 'action'} />
                          <Typography variant="body2" fontWeight={additionalInfo.markSecondNightPaid ? 'bold' : 'normal'}>
                            Sí, marcar saldo como pagado ({formatCurrency(selected?.secondNightPayment || 0)})
                          </Typography>
                        </Stack>
                      }
                    />
                    {additionalInfo.markSecondNightPaid && (
                      <Alert severity="success" sx={{ mt: 1 }} icon={<CheckCircleIcon />}>
                        Se marcará el pago como <strong>completo</strong> al generar el PDF.
                      </Alert>
                    )}
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          )}

          {/* Si la 2da noche ya está pagada, mostrar badge informativo */}
          {selected?.paymentStatus === 'partial' && selected?.secondNightNotePaid && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckCircleIcon />}>
                La segunda noche ya fue marcada como pagada anteriormente.
              </Alert>
            </Grid>
          )}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" startIcon={<ClearIcon />} variant="outlined"
            onClick={() => setAdditionalInfo({ ciudad: '', estado: '', includeBreakfast: false, markSecondNightPaid: false })}>
            Limpiar campos
          </Button>
        </Box>

        {(!additionalInfo.ciudad.trim() || !additionalInfo.estado.trim()) && (
          <Alert severity="warning">Completa ciudad y estado para continuar.</Alert>
        )}
      </Box>
    );
  };

  // PASO 3: Confirmación — muestra la firma guardada como imagen <img>
  const StepConfirmContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="success" icon={<DrawIcon />}>
        <Typography variant="subtitle2" fontWeight="bold">
          Todo listo. Revisa el resumen y genera el PDF de check-in.
        </Typography>
        <Typography variant="caption">
          La firma que proporcionaste en las políticas se incluirá en las hojas 1 y 3 del documento.
        </Typography>
      </Alert>

      {selected && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Resumen del check-in</Typography>
          <Typography variant="body2"><strong>Reserva:</strong> {selected.bookingId}</Typography>
          <Typography variant="body2"><strong>Huésped:</strong> {selected.guestInfo?.firstName} {selected.guestInfo?.lastName}</Typography>
          <Typography variant="body2"><strong>Ciudad:</strong> {additionalInfo.ciudad} &nbsp;|&nbsp; <strong>Estado:</strong> {additionalInfo.estado}</Typography>
          <Typography variant="body2"><strong>Check-in:</strong> {formatDateLong(selected.checkIn)} &nbsp;→&nbsp; <strong>Check-out:</strong> {formatDateLong(selected.checkOut)}</Typography>
          <Typography variant="body2"><strong>Desayuno:</strong> {additionalInfo.includeBreakfast ? 'Incluye' : 'No incluye'}</Typography>
          <Typography variant="body2"><strong>Total:</strong> {formatCurrency(selected.totalPrice)}</Typography>
          {selected.paymentStatus === 'partial' && (
            <>
              <Typography variant="body2"><strong>Anticipo:</strong> {formatCurrency(selected.initialPayment)}</Typography>
              <Typography variant="body2"><strong>Saldo:</strong> {formatCurrency(selected.secondNightPayment)}</Typography>
            </>
          )}
          {additionalInfo.markSecondNightPaid && (
            <Chip label="✓ Saldo liquidado en este check-in" color="success" size="small" sx={{ mt: 1 }} />
          )}
        </Paper>
      )}

      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Vista previa de la firma registrada
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Esta firma aparecerá en la hoja 1 (check-in) y en la hoja 3 (políticas continuación) del PDF.
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 1,
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'divider',
            display: 'inline-block',
            width: '100%',
            maxWidth: 500,
          }}
        >
          {signatureDataURL ? (
            <img
              src={signatureDataURL}
              alt="Firma del huésped"
              style={{
                border: '1px solid #ccc',
                backgroundColor: 'white',
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="error">
                No se encontró la firma. Por favor regresa al paso 1 y firma de nuevo.
              </Typography>
            </Box>
          )}
        </Paper>

        {signatureDataURL ? (
          <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
            ✓ Firma capturada correctamente
          </Typography>
        ) : (
          <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
            ✗ Firma no disponible — regresa al paso 1
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Renderizado del contenido por paso
  // ─────────────────────────────────────────────────────────────────────────
  const renderStepContent = () => {
    switch (checkinStep) {
      case 1: return <StepPoliciesContent />;
      case 2: return <StepReservationDataContent />;
      case 3: return <StepConfirmContent />;
      default: return null;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ManageAccountsIcon />
        <Typography variant="h6">Administración de reservas</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Typography variant="body2" sx={{ mb: 3 }}>
        Ver, editar, cancelar y gestionar reservas. Detalle muestra cliente, habitación, fechas y pagos.
      </Typography>

      {/* Barra de búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField fullWidth placeholder="Buscar por ID, nombre, email o habitación..." value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: filters.search && <InputAdornment position="end"><IconButton size="small" onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}><ClearIcon /></IconButton></InputAdornment>,
              }}
              variant="outlined" size="small" />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Filtros rápidos por estado:</Typography>
              <ToggleButtonGroup value={filters.status} onChange={(_, v) => setFilters((prev) => ({ ...prev, status: v }))} size="small" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <ToggleButton value="active" selected={filters.status.includes('active')}
                  sx={{ borderColor: 'success.light', '&.Mui-selected': { backgroundColor: 'success.main', color: 'white', '&:hover': { backgroundColor: 'success.dark' } } }}>
                  <Stack direction="row" alignItems="center" spacing={1}><CheckCircleIcon fontSize="small" /><Typography variant="body2">Activas</Typography></Stack>
                </ToggleButton>
                <ToggleButton value="cancelled" selected={filters.status.includes('cancelled')}
                  sx={{ borderColor: 'error.light', '&.Mui-selected': { backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' } } }}>
                  <Stack direction="row" alignItems="center" spacing={1}><CancelIcon fontSize="small" /><Typography variant="body2">Canceladas</Typography></Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Filtros rápidos por pago:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {['completed', 'partial'].map((p) => (
                  <Chip key={p} label={p === 'completed' ? 'Pago Completo' : 'Pago Parcial'} clickable
                    color={filters.paymentStatus.includes(p) ? (p === 'completed' ? 'success' : 'warning') : 'default'}
                    variant={filters.paymentStatus.includes(p) ? 'filled' : 'outlined'}
                    onClick={() => setFilters((prev) => ({ ...prev, paymentStatus: prev.paymentStatus.includes(p) ? prev.paymentStatus.filter((x) => x !== p) : [...prev.paymentStatus, p] }))}
                    onDelete={filters.paymentStatus.includes(p) ? () => setFilters((prev) => ({ ...prev, paymentStatus: prev.paymentStatus.filter((x) => x !== p) })) : undefined}
                    deleteIcon={<ClearIcon />}
                    icon={p === 'completed' ? <PaidIcon fontSize="small" /> : <PendingIcon fontSize="small" />}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {activeFiltersCount > 0 && <ActiveFilterChips />}
      <AdvancedFilters />

      {/* Estadísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { value: stats.total, label: 'Reservas totales', color: 'primary' },
          { value: stats.active, label: 'Activas', color: 'success.main' },
          { value: formatCurrency(stats.totalRevenue), label: 'Ingreso total', color: 'warning.main' },
          { value: stats.completedPayment, label: 'Pagos completos', color: 'info.main' },
          { value: stats.pendingSecondNight, label: '2da noche pendiente', color: 'error.main' },
          { value: stats.cancelled, label: 'Canceladas', color: 'text.secondary' },
        ].map((stat, idx) => (
          <Grid item xs={6} sm={4} md={2} key={idx}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color={stat.color}>{stat.value}</Typography>
                <Typography variant="caption" color="textSecondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Mostrando {filteredReservas.length} de {reservas.length} reservas{activeFiltersCount > 0 && ' (filtradas)'}
        </Typography>
        {activeFiltersCount > 0 && <Button startIcon={<FilterAltOffIcon />} size="small" onClick={clearAllFilters} variant="text">Limpiar filtros</Button>}
      </Box>

      {/* Tabla */}
      {loading && !viewDialogOpen && !editingDialogOpen ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID Reserva</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Habitación</TableCell>
                <TableCell>Fechas</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Pago</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReservas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                      <FilterListIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography variant="body1" color="text.secondary">No se encontraron reservas</Typography>
                      {activeFiltersCount > 0 && <Button variant="outlined" onClick={clearAllFilters} startIcon={<ClearIcon />}>Limpiar filtros</Button>}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReservas.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell><Typography variant="body2" fontWeight="bold">{r.bookingId}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.guestInfo?.firstName} {r.guestInfo?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.guestInfo?.email}</Typography>
                    </TableCell>
                    <TableCell>{r.roomName}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDate(r.checkIn)} → {formatDate(r.checkOut)}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">{r.nights} {r.nights === 1 ? 'noche' : 'noches'}</Typography>
                    </TableCell>
                    <TableCell>{formatCurrency(r.totalPrice)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip label={r.paymentStatus} size="small" color={chipColorForPayment(r.paymentStatus)} />
                        {r.paymentStatus === 'partial' && <Typography variant="caption" color="text.secondary">{r.secondNightNotePaid ? '✅ Pagada' : '⏳ Pendiente'}</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell><Chip label={r.status} size="small" color={chipColorForStatus(r.status)} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => openView(r)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(r)} disabled={loading}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Descargar voucher"><IconButton size="small" onClick={() => handleDownloadVoucher(r.bookingId)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Reenviar voucher por email">
                          <IconButton size="small" color="secondary" onClick={() => handleResendVoucher(r.bookingId, r.guestInfo?.email)} disabled={resendingVoucherId === r.bookingId}>
                            {resendingVoucherId === r.bookingId ? <CircularProgress size={16} /> : <ForwardToInboxIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generar check-in (3 páginas)">
                          <IconButton size="small" color="info" onClick={() => openSignatureDialog(r)}><AssignmentIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        {r.status !== 'cancelled' && (
                          <Tooltip title="Cancelar reserva"><IconButton size="small" color="error" onClick={() => handleCancelReserva(r)} disabled={loading}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {r.paymentStatus === 'partial' && !r.secondNightNotePaid && (
                          <Tooltip title="Marcar 2da noche pagada"><IconButton size="small" color="success" onClick={() => handleMarkSecondNightPaid(r)} disabled={loading}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {r.secondNightNotePaid && (
                          <Tooltip title="Anular pago de 2da noche"><IconButton size="small" color="warning" onClick={() => handleUnmarkSecondNightPaid(r)} disabled={loading}><UndoIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── View Dialog ── */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de reserva</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>{selected.bookingId}</Typography>
                <Chip label={selected.status} color={chipColorForStatus(selected.status)} size="small" />
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Información del huésped</Typography>
                <Typography variant="body2"><strong>Nombre:</strong> {selected.guestInfo?.firstName} {selected.guestInfo?.lastName}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {selected.guestInfo?.email}</Typography>
                <Typography variant="body2"><strong>Teléfono:</strong> {selected.guestInfo?.phone || 'N/A'}</Typography>
                {selected.guestInfo?.specialRequests && <Typography variant="body2"><strong>Solicitudes especiales:</strong> {selected.guestInfo.specialRequests}</Typography>}
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Detalles de la reserva</Typography>
                <Typography variant="body2"><strong>Habitación:</strong> {selected.roomName}</Typography>
                <Typography variant="body2"><strong>Check-in:</strong> {formatDate(selected.checkIn)}</Typography>
                <Typography variant="body2"><strong>Check-out:</strong> {formatDate(selected.checkOut)}</Typography>
                <Typography variant="body2"><strong>Noches:</strong> {selected.nights}</Typography>
                <Typography variant="body2"><strong>Precio por noche:</strong> {formatCurrency(selected.pricePerNight)}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Información de pago</Typography>
                <Typography variant="body2"><strong>Subtotal:</strong> {formatCurrency(selected.subtotal)}</Typography>
                <Typography variant="body2"><strong>IVA (16%):</strong> {formatCurrency(selected.tax)}</Typography>
                <Typography variant="body2"><strong>Total:</strong> {formatCurrency(selected.totalPrice)}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}><strong>Pago inicial (50%):</strong> {formatCurrency(selected.initialPayment)}</Typography>
                <Typography variant="body2"><strong>Pendiente (2da noche):</strong> {formatCurrency(selected.secondNightPayment)}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip label={`Estado: ${selected.paymentStatus}`} color={chipColorForPayment(selected.paymentStatus)} size="small" />
                  {selected.secondNightNotePaid && <Chip label="2da noche pagada ✓" color="success" size="small" />}
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Información adicional</Typography>
                <Typography variant="body2"><strong>Fecha de creación:</strong> {formatDate(selected.createdAt)}</Typography>
                {selected.stripePaymentIntentId && <Typography variant="caption" display="block" color="text.secondary">Payment Intent: {selected.stripePaymentIntentId}</Typography>}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Cerrar</Button>
          {selected && (
            <>
              <Button variant="outlined" startIcon={<AssignmentIcon />} onClick={() => { setViewDialogOpen(false); openSignatureDialog(selected); }}>Generar Check-in</Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleDownloadVoucher(selected.bookingId)}>Descargar Voucher</Button>
              <Button variant="outlined" color="secondary"
                startIcon={resendingVoucherId === selected.bookingId ? <CircularProgress size={16} /> : <ForwardToInboxIcon />}
                onClick={() => handleResendVoucher(selected.bookingId, selected.guestInfo?.email)}
                disabled={resendingVoucherId === selected.bookingId}>
                Reenviar Voucher
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={editingDialogOpen} onClose={() => setEditingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar reserva</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Nombre" value={editForm.guestInfo?.firstName || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, guestInfo: { ...prev.guestInfo, firstName: e.target.value } }))} disabled={loading} fullWidth />
              <TextField label="Apellido" value={editForm.guestInfo?.lastName || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, guestInfo: { ...prev.guestInfo, lastName: e.target.value } }))} disabled={loading} fullWidth />
              <TextField label="Email" type="email" value={editForm.guestInfo?.email || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, guestInfo: { ...prev.guestInfo, email: e.target.value } }))} disabled={loading} fullWidth />
              <TextField label="Teléfono" value={editForm.guestInfo?.phone || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, guestInfo: { ...prev.guestInfo, phone: e.target.value } }))} disabled={loading} fullWidth />
              <Stack direction="row" spacing={2}>
                <TextField label="Check-in" type="date" value={editForm.checkIn} onChange={(e) => setEditForm((prev) => ({ ...prev, checkIn: e.target.value }))} InputLabelProps={{ shrink: true }} disabled={loading} fullWidth />
                <TextField label="Check-out" type="date" value={editForm.checkOut} onChange={(e) => setEditForm((prev) => ({ ...prev, checkOut: e.target.value }))} InputLabelProps={{ shrink: true }} disabled={loading} fullWidth />
              </Stack>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={editForm.status} label="Estado" onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))} disabled={loading}>
                  <MenuItem value="active">Activa</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingDialogOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSaveEdit} disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Check-in Dialog (3 pasos) ── */}
      <Dialog open={signatureDialogOpen} onClose={closeSignatureDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignmentIcon />
            <Typography variant="h6">Check-in — {selected?.bookingId}</Typography>
          </Stack>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Stepper activeStep={checkinStep - 1} alternativeLabel>
              {checkinSteps.map((label, idx) => (
                <Step key={label} completed={checkinStep - 1 > idx}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {renderStepContent()}
        </DialogContent>

        <DialogActions>
          {/* Cancelar siempre disponible */}
          <Button onClick={closeSignatureDialog} disabled={generatingCheckin}>Cancelar</Button>

          {/* Atrás (disponible desde paso 2) */}
          {checkinStep > 1 && (
            <Button onClick={() => setCheckinStep((s) => s - 1)} variant="outlined" disabled={generatingCheckin}>
              Atrás
            </Button>
          )}

          {/* Siguiente / Generar */}
          {checkinStep < 3 ? (
            <Button
              variant="contained"
              onClick={handleNextStep}
              disabled={!canProceedFromStep(checkinStep) || generatingCheckin}
              color="primary"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={generatingCheckin ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={generateCheckinPDF}
              disabled={!signatureDataURL || !additionalInfo.ciudad.trim() || !additionalInfo.estado.trim() || generatingCheckin}
              color="success"
            >
              {generatingCheckin ? 'Generando PDF...' : 'Generar Check-in PDF'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}